// skipcq: JS-W1028, JS-0128
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  collection,
  getDocs,
  limit,
  orderBy,
  query,
  startAfter,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { db, functions } from '../../utils/firebase';
import { CRAWL_PLANNER_RUNS_COLLECTION } from '../../utils/firestoreCollections';
import {
  formatTs,
  cohortHistogramEntriesSorted,
  formatCohortBarCaption,
  formatSampleFraction,
  mapPlannerRunDoc,
} from './planningTabUtils';
import { CRAWL_TYPES } from './scheduleFormConstants';
import CostControlsPanel from './CostControlsPanel';
import { isCrawlerSchedulerCostControlsVisible } from './crawlerSchedulerCostControlsEnv';

const PAGE_SIZE = 24;

/** @param {{ row: object }} props */
function PlanningTableRow({ row }) {
  const statesPreview = row.eligibleStatesOrdered.join(', ');
  return (
    <TableRow>
      <TableCell>{formatTs(row.startedAt)}</TableCell>
      <TableCell>{formatTs(row.finishedAt)}</TableCell>
      <TableCell>{row.dryRun ? 'Yes' : 'No'}</TableCell>
      <TableCell>{row.crawlType}</TableCell>
      <TableCell>{row.cadence}</TableCell>
      <TableCell sx={{ maxWidth: 200 }} title={statesPreview}>
        {statesPreview.length > 64 ? `${statesPreview.slice(0, 61)}…` : statesPreview}
      </TableCell>
      <TableCell>{row.breweriesDiscovered}</TableCell>
      <TableCell>{row.schedulesUpserted}</TableCell>
      <TableCell>{row.schedulesSkippedPaused}</TableCell>
      <TableCell>{row.schedulesSkippedErrorSuppressed}</TableCell>
      <TableCell>{row.schedulesSkippedLongTermOob}</TableCell>
      <TableCell>{formatSampleFraction(row.sampleFraction)}</TableCell>
      <TableCell>{row.schedulesSkippedSample}</TableCell>
    </TableRow>
  );
}

function PlanningTableColumnHeaders() {
  return (
    <TableHead>
      <TableRow>
        <TableCell>Started</TableCell>
        <TableCell>Finished</TableCell>
        <TableCell>Dry run</TableCell>
        <TableCell>Crawl type</TableCell>
        <TableCell>Cadence</TableCell>
        <TableCell>Eligible states</TableCell>
        <TableCell>Breweries</TableCell>
        <TableCell>Upserted</TableCell>
        <TableCell>Skipped (paused)</TableCell>
        <TableCell>Skipped (suppressed)</TableCell>
        <TableCell>Skipped (long-term OOB)</TableCell>
        <TableCell>Sample fraction</TableCell>
        <TableCell>Skipped (sample)</TableCell>
      </TableRow>
    </TableHead>
  );
}

/** @param {{ rows: Array<ReturnType<typeof mapPlannerRunDoc>> }} props */
function PlanningTableBody({ rows }) {
  if (rows.length === 0) {
    return (
      <TableBody>
        <TableRow>
          <TableCell colSpan={13}>
            <Typography variant="body2" color="text.secondary">
              No planner runs yet. Scheduled runs append here; use Run replan for an on-demand pass.
            </Typography>
          </TableCell>
        </TableRow>
      </TableBody>
    );
  }
  return (
    <TableBody>
      {rows.map(function plannerRunRow(planningRow) {
        return <PlanningTableRow key={planningRow.id} row={planningRow} />;
      })}
    </TableBody>
  );
}

/** @param {{ rows: Array<ReturnType<typeof mapPlannerRunDoc>> }} props */
function PlanningRunsTable({ rows }) {
  return (
    <TableContainer component={Paper} variant="outlined" sx={{ width: '100%' }}>
      <Table size="small" sx={{ width: '100%' }}>
        <PlanningTableColumnHeaders />
        <PlanningTableBody rows={rows} />
      </Table>
    </TableContainer>
  );
}

function PlanningPagerBar({ page, rowsLength, hasNext, onPrevious, onNext }) {
  return (
    <Box display="flex" alignItems="center" justifyContent="space-between" mt={2}>
      <Typography variant="body2" color="text.secondary">
        Page {page + 1}
        {rowsLength ? ` · ${rowsLength} row(s)` : ''}
      </Typography>
      <Box display="flex" gap={1}>
        <Button variant="outlined" disabled={page === 0} onClick={onPrevious}>
          Previous
        </Button>
        <Button variant="outlined" disabled={!hasNext} onClick={onNext}>
          Next
        </Button>
      </Box>
    </Box>
  );
}

/** @param {{ cohortHistogram: Record<string, number>, eligibleStatesOrdered: string[] }} props */
function LatestCohortCoveragePanel({ cohortHistogram, eligibleStatesOrdered }) {
  const entries = cohortHistogramEntriesSorted(cohortHistogram);
  if (entries.length === 0) {
    return (
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          Coverage by state (latest run — first table row)
        </Typography>
        <Typography variant="body2" color="text.secondary">
          No state cohort counts recorded for this run.
        </Typography>
      </Paper>
    );
  }
  let maxCount = 0;
  for (const [, count] of entries) {
    if (count > maxCount) maxCount = count;
  }

  const barPairs = [];
  let barRowIndex = 0;
  for (const pair of entries) {
    barRowIndex += 1;
    const cohortLabel = pair[0];
    const cohortCount = pair[1];
    const percent = maxCount > 0 ? Math.round((cohortCount / maxCount) * 100) : 0;

    barPairs.push(
      <Box key={`${cohortLabel}-${barRowIndex}`} sx={{ mb: 1 }}>
        <Typography variant="caption" color="text.secondary">
          {formatCohortBarCaption(cohortLabel, cohortCount, eligibleStatesOrdered)}
        </Typography>
        <LinearProgress variant="determinate" value={percent} sx={{ mt: 0.25, height: 8 }} />
      </Box>,
    );
  }

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
      <Typography variant="subtitle2" gutterBottom>
        Coverage by state (latest run — first table row)
      </Typography>
      {barPairs}
    </Paper>
  );
}

function PlanningTab() {
  const planCrawlSchedules = useMemo(
    () =>
      httpsCallable(functions, 'callableSchedulerPlanCrawlSchedules-planCrawlSchedules', {
        timeout: 600_000,
      }),
    [],
  );

  const [page, setPage] = useState(0);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasNext, setHasNext] = useState(false);
  const lastDocPerPage = useRef([]);
  const latestLoadIdRef = useRef(0);

  const [dryRun, setDryRun] = useState(false);
  const [replanCrawlType, setReplanCrawlType] = useState('');
  const [plannerOperationBusy, setPlannerOperationBusy] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  const loadPage = useCallback(async function loadPageCb(targetPage) {
    const loadId = latestLoadIdRef.current + 1;
    latestLoadIdRef.current = loadId;
    setLoading(true);
    setError(null);
    try {
      const runsCollection = collection(db, CRAWL_PLANNER_RUNS_COLLECTION);
      if (targetPage === 0) {
        lastDocPerPage.current = [];
      }
      const previousLastDoc = targetPage === 0 ? null : lastDocPerPage.current[targetPage - 1];
      if (targetPage > 0 && !previousLastDoc) {
        return;
      }
      const listQuery =
        targetPage === 0
          ? query(runsCollection, orderBy('startedAt', 'desc'), limit(PAGE_SIZE + 1))
          : query(
              runsCollection,
              orderBy('startedAt', 'desc'),
              startAfter(previousLastDoc),
              limit(PAGE_SIZE + 1),
            );
      const snap = await getDocs(listQuery);
      if (loadId !== latestLoadIdRef.current) {
        return;
      }
      const docs = snap.docs;
      const more = docs.length > PAGE_SIZE;
      const pageDocs = more ? docs.slice(0, PAGE_SIZE) : docs;
      const mapped = pageDocs.map(mapPlannerRunDoc);
      if (pageDocs.length > 0) {
        lastDocPerPage.current[targetPage] = pageDocs[pageDocs.length - 1];
        lastDocPerPage.current.length = targetPage + 1;
      } else {
        lastDocPerPage.current[targetPage] = null;
      }
      setRows(mapped);
      setHasNext(more);
      setPage(targetPage);
    } catch (err) {
      if (loadId !== latestLoadIdRef.current) {
        return;
      }
      console.error(err);
      setError(err);
    } finally {
      if (loadId === latestLoadIdRef.current) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    loadPage(0);
  }, [loadPage]);

  const handlePrevPage = useCallback(() => {
    loadPage(page - 1);
  }, [loadPage, page]);

  const handleNextPage = useCallback(() => {
    loadPage(page + 1);
  }, [loadPage, page]);

  const latestRow = rows.length > 0 ? rows[0] : null;

  const handleDryRunChange = useCallback((event) => {
    setDryRun(event.target.checked === true);
  }, []);

  const handleReplanCrawlTypeChange = useCallback((event) => {
    setReplanCrawlType(event.target.value);
  }, []);

  const handleCloseSnackbar = useCallback((_event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar((previous) => ({ ...previous, open: false }));
  }, []);

  const handleCostControlSuccess = useCallback((message) => {
    setSnackbar({
      open: true,
      message,
      severity: 'success',
    });
  }, []);

  const handleCostControlError = useCallback((firebaseMessage) => {
    setSnackbar({
      open: true,
      message: `Cost control failed: ${firebaseMessage}`,
      severity: 'error',
    });
  }, []);

  const handleCostControlOperationComplete = useCallback(() => loadPage(0), [loadPage]);

  const showCostControls = isCrawlerSchedulerCostControlsVisible();

  const handleRunReplan = useCallback(async () => {
    setPlannerOperationBusy(true);
    try {
      const result = await planCrawlSchedules({
        dryRun,
        ...(replanCrawlType ? { crawlType: replanCrawlType } : {}),
      });
      const data = result.data;
      const upserted = data && typeof data.schedulesUpserted === 'number' ? data.schedulesUpserted : 0;
      const breweries =
        data && typeof data.breweriesDiscovered === 'number' ? data.breweriesDiscovered : 0;
      const dryRunRan = data && data.dryRun === true;

      const message = dryRunRan
        ? `Dry run complete (${breweries} breweries surveyed; ${upserted} schedule upserts would apply).`
        : `Replan finished (${breweries} breweries; ${upserted} schedules upserted).`;

      setSnackbar({
        open: true,
        message,
        severity: 'success',
      });
      await loadPage(0);
    } catch (err) {
      console.error(err);
      const firebaseMessage =
        err && typeof err === 'object' && 'message' in err ? String(err.message) : String(err);
      setSnackbar({
        open: true,
        message: `Replan failed: ${firebaseMessage}`,
        severity: 'error',
      });
    } finally {
      setPlannerOperationBusy(false);
    }
  }, [dryRun, loadPage, planCrawlSchedules, replanCrawlType]);

  const errorBanner = error ? (
    <Typography color="error" sx={{ mb: 2 }}>
      Unable to load planner runs. You can try again; details are in the browser console.
    </Typography>
  ) : null;

  const cohortPanel =
    loading || error ? null : (
      <LatestCohortCoveragePanel
        cohortHistogram={latestRow?.cohortHistogram ?? {}}
        eligibleStatesOrdered={latestRow?.eligibleStatesOrdered ?? []}
      />
    );

  const mainContent = loading ? (
    <Box display="flex" justifyContent="center" py={4}>
      <CircularProgress />
    </Box>
  ) : error ? null : (
    <>
      <PlanningRunsTable rows={rows} />
      <PlanningPagerBar
        page={page}
        rowsLength={rows.length}
        hasNext={hasNext}
        onPrevious={handlePrevPage}
        onNext={handleNextPage}
      />
    </>
  );

  const costControlsPanel = showCostControls ? (
    <CostControlsPanel
      planCrawlSchedules={planCrawlSchedules}
      crawlTypeFilter={replanCrawlType}
      onCrawlTypeFilterChange={handleReplanCrawlTypeChange}
      dryRun={dryRun}
      busy={plannerOperationBusy}
      setBusy={setPlannerOperationBusy}
      onOperationComplete={handleCostControlOperationComplete}
      onSuccess={handleCostControlSuccess}
      onError={handleCostControlError}
    />
  ) : null;

  return (
    <Box>
      {costControlsPanel}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          mb: 2,
        }}
      >
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 640 }}>
          Deterministic planner runs update <code>crawlSchedules</code> (next due, cohort, stable slot).
          Requires global admin; App Check applies.
        </Typography>
        <Box display="flex" flexWrap="wrap" gap={2} alignItems="center">
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel id="replan-crawl-type-label">Planner</InputLabel>
            <Select
              labelId="replan-crawl-type-label"
              label="Planner"
              value={replanCrawlType}
              onChange={handleReplanCrawlTypeChange}
            >
              <MenuItem value="">All crawl types</MenuItem>
              {CRAWL_TYPES.map((crawlTypeOption) => (
                <MenuItem key={crawlTypeOption} value={crawlTypeOption}>
                  {crawlTypeOption}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControlLabel
            control={<Checkbox checked={dryRun} onChange={handleDryRunChange} color="primary" />}
            label="Dry run"
          />
          <Button
            variant="contained"
            onClick={handleRunReplan}
            disabled={plannerOperationBusy}
          >
            {plannerOperationBusy ? 'Running…' : 'Run replan'}
          </Button>
        </Box>
      </Box>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={8000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
      {cohortPanel}
      {errorBanner}
      {mainContent}
    </Box>
  );
}

export default PlanningTab;
